import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { skuConfigApi } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;

const SKUConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();

  // 获取SKU配置列表
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const data = await skuConfigApi.getConfigs();
      console.log('获取到的SKU配置数据:', data);
      setConfigs(data.items || []);
    } catch (error) {
      message.error('获取SKU配置失败');
      console.error('获取SKU配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // 创建或更新配置
  const handleSubmit = async (values) => {
    try {
      console.log('提交数据:', values);
      
      if (editingConfig) {
        await skuConfigApi.updateConfig(editingConfig.id, values);
        message.success('更新成功');
      } else {
        await skuConfigApi.createConfig(values);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      setEditingConfig(null);
      form.resetFields();
      fetchConfigs();
    } catch (error) {
      message.error(editingConfig ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 删除配置
  const handleDelete = async (id) => {
    try {
      await skuConfigApi.deleteConfig(id);
      message.success('删除成功');
      fetchConfigs();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 激活配置
  const handleActivate = async (id) => {
    try {
      await skuConfigApi.activateConfig(id);
      message.success('激活成功');
      fetchConfigs();
    } catch (error) {
      message.error('激活失败');
      console.error('激活失败:', error);
    }
  };

  // 打开编辑模态框
  const showEditModal = (config) => {
    setEditingConfig(config);
    form.setFieldsValue(config);
    setModalVisible(true);
  };

  // 打开创建模态框
  const showCreateModal = () => {
    setEditingConfig(null);
    form.resetFields();
    setModalVisible(true);
  };

  const columns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '默认前缀',
      dataIndex: 'sku_prefix',
      key: 'sku_prefix',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '特殊前缀',
      dataIndex: 'sku_special_prefix',
      key: 'sku_special_prefix',
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Space>
          <Tag color={isActive ? 'green' : 'default'}>
            {isActive ? '激活' : '未激活'}
          </Tag>
          {record.is_default && <Tag color="orange">默认</Tag>}
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.is_active && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleActivate(record.id)}
            >
              激活
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          {!record.is_default && (
            <Popconfirm
              title="确定要删除这个配置吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={3}>SKU配置管理</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showCreateModal}
            >
              新建配置
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={configs}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />

        <Modal
          title={editingConfig ? '编辑SKU配置' : '新建SKU配置'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingConfig(null);
            form.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              sku_prefix: '',
              sku_special_prefix: '',
            }}
          >
            <Form.Item
              name="name"
              label="配置名称"
              rules={[{ required: true, message: '请输入配置名称' }]}
            >
              <Input placeholder="请输入配置名称" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="sku_prefix"
                  label="默认前缀"
                  rules={[{ required: true, message: '请输入默认前缀' }]}
                >
                  <Input placeholder="BYHJYAIYANYI" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="sku_special_prefix"
                  label="特殊前缀"
                  rules={[{ required: true, message: '请输入特殊前缀' }]}
                >
                  <Input placeholder="FTRYUIKSDH" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="请输入配置描述" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingConfig ? '更新' : '创建'}
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    setEditingConfig(null);
                    form.resetFields();
                  }}
                >
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default SKUConfig;